import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { Subject, takeUntil } from 'rxjs';

import { AppStateService } from 'src/app/core-module/services/app-state.service';
import { CheckboxStateService } from "../../../core-module/services/state/checkbox-state.service";
import { FeatureDefaultsStateService } from "../../../core-module/services/state/feature-defaults-state.service";
import { FeatureFlagsStateService } from "../../../core-module/services/state/feature-flags-state.service";

interface AuxOutputProperties {
  OutputVoltage?: {
    enum?: string[];
    default?: string;
  };
}

interface AuxOutputValue {
  OutputVoltage: string;
}

@Component({
  standalone: false,
  selector: 'app-auxoutput',
  templateUrl: './auxoutput.component.html',
  styleUrls: ['./auxoutput.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuxoutputComponent extends FieldType implements OnInit, OnDestroy {
  selectedOutputVoltage = '';
  outputVoltages: string[] = [];
  defaultOutputVoltage = '';
  defaultColor = true;
  isIncludedInConfiguration = false;
  isEdited = false;
  private ignoreParentUpdate = false;
  private hasConfigData = false;
  private properties?: AuxOutputProperties;
  private readonly destroy$ = new Subject<void>();

  constructor(
    
    private readonly ref: ChangeDetectorRef,
    private readonly appStateService: AppStateService,
    private featureDefaults: FeatureDefaultsStateService,
    private featureFlags: FeatureFlagsStateService,
    private checkboxState: CheckboxStateService
  ) {
    super();
  }

  ngOnInit(): void {
    // Protect initial load from parent checkbox updates
    this.ignoreParentUpdate = true;

    // Get schema properties
    this.properties = (this.field as { properties?: AuxOutputProperties })?.properties;

    // Get output voltage options from schema enum
    if (this.properties?.OutputVoltage?.enum) {
      this.outputVoltages = this.properties.OutputVoltage.enum;
    }

    // Get default value from schema
    if (this.properties?.OutputVoltage?.default) {
      this.defaultOutputVoltage = this.properties.OutputVoltage.default;
    }

    // Load saved configuration data
    const configData = this.appStateService.getConfigurationData();
    const isAuxSelectedInConfig = Array.isArray(configData?.selectedFeaturesInfo)
      ? configData.selectedFeaturesInfo.some((feature: unknown) => {
          const value = String(feature || "").trim().toLowerCase();
          return value === "aux-" || value === "aux";
        })
      : false;
    let savedValue = null;

    // Check UnSelectedFeatConfig first (for edited but unselected features)
    const unSelectedFeat = this.appStateService.getUnSelectedFeatConfig();
    let isConfigEdited = false;

    if (unSelectedFeat != null && Array.isArray(unSelectedFeat)) {
      let featIndex = 0;
      unSelectedFeat.forEach((element) => {
        if (element["featureName"] && element["featureName"].toString().toUpperCase() === "AUX") {
          isConfigEdited = true;
          savedValue = element["featureParameters"][0];
          unSelectedFeat.splice(featIndex, 1);
          this.appStateService.setUnSelectedFeatConfig(unSelectedFeat);
        } else {
          featIndex++;
        }
      });
    }

    // If not in UnSelectedFeatConfig, check configData
    if (!isConfigEdited && configData) {
      try {
        const auxData = configData?.resp?.AUX || configData?.resp?.Aux;
        if (auxData) {
          savedValue = auxData;
          this.hasConfigData = true;
        }
      } catch (e) {
        console.error('Error parsing configuration data from AppStateService', e);
      }
    }

    // Use centralized method to get feature data (prioritizes SaveSetData over ConfigurationData)
    if (!savedValue) {
      savedValue = this.appStateService.getFeatureData('AUX');
      if (savedValue) {
        this.hasConfigData = true;
      }
    }

    // Subscribe to restore default trigger
    this.featureDefaults.getAuxDefaultValue
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response === "valid") {
          this.defaultColor = true;
          this.resetToDefaults();
        }
      });

    let currentValue = savedValue || this.formControl.value || {};

    // Subscribe to parent "Include in configuration" checkbox changes
    this.featureFlags.getAuxFeatApi
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state !== null && !this.ignoreParentUpdate) {
          this.isIncludedInConfiguration = !!state;

          if (!this.isIncludedInConfiguration) {
            this.hasConfigData = false;
            currentValue = {};
            this.updateFormControl();
            this.ref.detectChanges();
            return;
          }

          // Handle checking: refresh flow
          if (this.hasConfigData) {
            // Refresh flow: preserve current state
            return;
          }

          this.updateFormControl();
          this.ref.detectChanges();
        }
      });

    // Load current value
    this.selectedOutputVoltage = currentValue.OutputVoltage ?? this.defaultOutputVoltage;

    this.updateFormControl();

    // Enable checkbox if value is non-default
    if (this.selectedOutputVoltage !== this.defaultOutputVoltage) {
      this.checkboxState.enableAuxCheckbox();
      this.isIncludedInConfiguration = true;
    }

    // Restore include state only when AUX is explicitly selected.
    // Presence of AUX payload alone should not auto-check include after Home->reopen.
    if (this.hasConfigData && isAuxSelectedInConfig) {
      this.checkboxState.enableAuxCheckbox();
      this.isIncludedInConfiguration = true;
    }

    this.checkDefaults();
    this.isEdited = false;
    this.ignoreParentUpdate = false;
  }

  onOutputVoltageChange(value: string): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    this.selectedOutputVoltage = value;

    // Auto-enable "Include in configuration" only when value is non-default
    if (this.selectedOutputVoltage !== this.defaultOutputVoltage) {
      this.checkboxState.enableAuxCheckbox();
      this.isIncludedInConfiguration = true;
    } else {
      // When value is default, don't automatically check the checkbox
      // Only disable it if it's not currently checked
      if (!this.isIncludedInConfiguration) {
        this.checkboxState.disableAuxCheckbox();
      }
    }

    this.updateFormControl();
    this.checkDefaults();
    this.ignoreParentUpdate = false;
  }

  private updateFormControl(): void {
    if (!this.formControl) {
      return;
    }

    const value: AuxOutputValue = {
      OutputVoltage: this.selectedOutputVoltage
    };

    this.formControl.setValue(value);

    // Save to configuration data after user edits
    if (this.isEdited) {
      this.setConfigurationData(value, 'AUX');
    }
  }

  checkDefaults(): void {
    this.defaultColor = this.selectedOutputVoltage === this.defaultOutputVoltage;
    this.featureDefaults.setAuxDefaultColor(this.defaultColor);
  }

  resetToDefaults(): void {

    this.selectedOutputVoltage = this.defaultOutputVoltage;
    this.updateFormControl();
    this.defaultColor = true;
    this.featureDefaults.setAuxDefaultColor(this.defaultColor);
    this.setConfigurationData(this.formControl.value, 'AUX');
    this.ref.detectChanges();
  }

  setConfigurationData(value: AuxOutputValue, keyVal: string): void {
    if (value != null) {
      // Use centralized method to save feature data
      // This automatically saves to both ConfigurationData and SaveSetData
      const auxObj = Object.assign({}, value);
      this.appStateService.setFeatureData(keyVal, auxObj);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
